import { getConfig } from '../config/environments.js';
import { logger } from '../logging/logger.js';
import { getAuthToken } from '../storage/token.js';
import { parseCurrencyToCents } from '../helpers/formatters.js'
import { ApiError } from './types.js';
import type { RequestConfig, RequestOptions } from './types.js';

interface ViteEnv {
  DEV?: boolean
  VITE_DEBUG_API?: string
}

interface ImportMetaWithEnv {
  env?: ViteEnv
}

const getDebugApiEnabled = (): boolean => {
  const meta = import.meta as ImportMetaWithEnv
  const viteEnv = meta.env
  return Boolean(viteEnv?.DEV) && viteEnv?.VITE_DEBUG_API === 'true'
}

const redactHeaders = (headers: HeadersInit | undefined): Record<string, string> | undefined => {
  if (!headers) return undefined
  // Convert HeadersInit to Record<string, string>
  const headerRecord: Record<string, string> = headers instanceof Headers
    ? Object.fromEntries(headers.entries())
    : Array.isArray(headers)
      ? Object.fromEntries(headers)
      : { ...headers }
  
  if (typeof headerRecord.Authorization === 'string' && headerRecord.Authorization.startsWith('Bearer ')) {
    headerRecord.Authorization = 'Bearer [REDACTED]'
  }
  return headerRecord
}

const isErrorWithMessage = (value: unknown): value is { message: string } => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'message' in value &&
    typeof (value as { message: unknown }).message === 'string'
  )
}

const makeAbortError = (): Error => {
  const err = new Error('Aborted')
  err.name = 'AbortError'
  return err
}

const UNKNOWN_ERROR_CODE = 'UNKNOWN_ERROR'

const getErrorCode = (value: unknown): string | null => {
  if (typeof value !== 'object' || value === null || !('code' in value)) {
    return null
  }
  const code = (value as { code?: unknown }).code
  if (typeof code === 'string' || typeof code === 'number') {
    return String(code)
  }
  return null
}

export const parseIntHeader = (headers: Headers, key: string, fallback?: number): number => {
  const raw = headers.get(key)
  if (!raw) return fallback ?? 0
  const parsed = Number.parseInt(raw, 10)
  return Number.isFinite(parsed) ? parsed : 0
}

export const parseOptionalIntHeader = (headers: Headers, key: string): number | null => {
  const raw = headers.get(key)
  if (!raw) return null
  const parsed = Number.parseInt(raw, 10)
  return Number.isFinite(parsed) ? parsed : null
}

export const parseCurrencyHeaderToCents = (headers: Headers, key: string): number | null => {
  const raw = headers.get(key)
  if (!raw) return null
  return parseCurrencyToCents(raw)
}

export const parseStringHeader = (headers: Headers, key: string, fallback?: string | null): string | null => {
  const raw = headers.get(key)
  if (!raw) return fallback ?? null
  return String(raw).trim()
}

export class ApiClient {
  private baseUrl: string;
  private defaultTimeout = 10000;

  constructor() {
    this.baseUrl = getConfig().apiBaseUrl;
  }

  private buildBody(body: unknown): BodyInit | undefined {
    if (body === undefined || body === null) return undefined
    if (body instanceof FormData) return body
    if (typeof body === 'string') return body
    if (body instanceof Blob) return body
    if (body instanceof ArrayBuffer) return body
    return JSON.stringify(body)
  }

  private normalizeHeaders(headers: Record<string, string> | undefined, body: unknown): Record<string, string> | undefined {
    if (!headers) return headers
    if (!(body instanceof FormData)) return headers
    // Browser will set multipart boundary automatically for FormData; ensure we don't override it.
    const filtered: Record<string, string> = {}
    for (const [key, value] of Object.entries(headers)) {
      if (key.toLowerCase() === 'content-type') continue
      filtered[key] = value
    }
    return filtered
  }

  /**
   * Parse a successful response body as JSON, tolerating empty bodies.
   *
   * A `204 No Content` or an otherwise empty body (e.g. an endpoint that
   * clears a resource) is a valid success response, but `response.json()`
   * throws `SyntaxError: Unexpected end of JSON input` on it. Callers whose
   * response type includes `void`/`undefined` rely on this returning
   * `undefined` instead of throwing.
   */
  private async parseJsonBody<T>(response: Response): Promise<T> {
    const text = await response.text()
    if (text.trim().length === 0) {
      return undefined as T
    }
    return JSON.parse(text) as T
  }

  private combineAbortSignals(...signals: AbortSignal[]): AbortSignal {
    const controller = new AbortController()
    for (const signal of signals) {
      if (signal.aborted) {
        controller.abort(signal.reason)
        return controller.signal
      }
      signal.addEventListener('abort', () => controller.abort(signal.reason), { once: true })
    }
    return controller.signal
  }

  async request<T>(endpoint: string, config: RequestConfig): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    
    const debugApi = getDebugApiEnabled()
    const externalSignal = config.signal
    const timeoutController = new AbortController()
    const timeoutMs = config.timeout ?? this.defaultTimeout
    const timeoutId = setTimeout(() => {
      timeoutController.abort()
    }, timeoutMs)

    // Combine external abort signal with internal timeout
    const combinedSignal = externalSignal
      ? this.combineAbortSignals(externalSignal, timeoutController.signal)
      : timeoutController.signal

    try {
      logger.debug('API Request:', { url, method: config.method });
      if (debugApi) {
        logger.debug('API Request details:', {
          url,
          method: config.method,
          headers: redactHeaders(config.headers),
          body: config.body ?? null,
        })
      }
      
      const response = await fetch(url, {
        method: config.method,
        headers: this.normalizeHeaders(config.headers, config.body),
        body: this.buildBody(config.body),
        signal: combinedSignal,
      })

      if (!response.ok) {
        const requestId =
          response.headers.get('x-request-id') ??
          response.headers.get('x-amzn-requestid') ??
          response.headers.get('x-amz-request-id') ??
          null

        // Try to parse error message from response body, falling back to text for non-JSON errors.
        let errorMessage = `API Error: ${response.status}`
        let errorCode = UNKNOWN_ERROR_CODE
        let errorBody: unknown = null
        let rawText: string | null = null
        try {
          rawText = await response.text()
        } catch {
          // ignore
        }
        const trimmedText = rawText?.trim() ?? ''
        if (trimmedText.length > 0 && !trimmedText.startsWith('<')) {
          try {
            errorBody = JSON.parse(trimmedText) as unknown
            if (isErrorWithMessage(errorBody)) {
              errorMessage = errorBody.message
            }
            const parsedCode = getErrorCode(errorBody)
            if (parsedCode) {
              errorCode = parsedCode
            }
          } catch {
            errorMessage = trimmedText.length > 200 ? `${trimmedText.slice(0, 200)}…` : trimmedText
          }
        }

        if (debugApi) {
          logger.debug('API Error response details:', {
            url,
            status: response.status,
            requestId,
            body: errorBody,
            text: trimmedText.length > 0 ? (trimmedText.length > 1000 ? `${trimmedText.slice(0, 1000)}…` : trimmedText) : null,
          })
        }
        const finalMessage = requestId ? `${errorMessage} (requestId: ${requestId})` : errorMessage
        throw new ApiError(finalMessage, response.status, errorCode);
      }

      const data = await this.parseJsonBody<T>(response);
      logger.debug('API Response:', { url, status: response.status });
      if (getDebugApiEnabled()) {
        logger.debug('API Response details:', {
          url,
          status: response.status,
          requestId:
            response.headers.get('x-request-id') ??
            response.headers.get('x-amzn-requestid') ??
            response.headers.get('x-amz-request-id') ??
            null,
        })
      }
      
      return data;
    } catch (error) {
      // Avoid noisy abort logs for intentional cancellations (including cases where fetch surfaces as a generic TypeError).
      if (combinedSignal.aborted) {
        // Log timeouts as warn; treat external aborts as expected.
        if (timeoutController.signal.aborted && !externalSignal?.aborted) {
          logger.warn('API Request timed out', { url, timeoutMs })
        }
        throw error instanceof Error && error.name === 'AbortError' ? error : makeAbortError()
      }

      logger.error('API Request failed:', { url, error });
      throw error;
    } finally {
      clearTimeout(timeoutId)
    }
  }

  async requestWithResponse<T>(endpoint: string, config: RequestConfig): Promise<{ data: T; response: Response }> {
    const url = `${this.baseUrl}${endpoint}`

    const debugApi = getDebugApiEnabled()
    const externalSignal = config.signal
    const timeoutController = new AbortController()
    const timeoutMs = config.timeout ?? this.defaultTimeout
    const timeoutId = setTimeout(() => {
      timeoutController.abort()
    }, timeoutMs)

    const combinedSignal = externalSignal
      ? this.combineAbortSignals(externalSignal, timeoutController.signal)
      : timeoutController.signal

    try {
      logger.debug('API Request:', { url, method: config.method })
      if (debugApi) {
        logger.debug('API Request details:', {
          url,
          method: config.method,
          headers: redactHeaders(config.headers),
          body: config.body ?? null,
        })
      }

      const response = await fetch(url, {
        method: config.method,
        headers: this.normalizeHeaders(config.headers, config.body),
        body: this.buildBody(config.body),
        signal: combinedSignal,
      })

      if (!response.ok) {
        const requestId =
          response.headers.get('x-request-id') ??
          response.headers.get('x-amzn-requestid') ??
          response.headers.get('x-amz-request-id') ??
          null

        let errorMessage = `API Error: ${response.status}`
        let errorCode = UNKNOWN_ERROR_CODE
        let errorBody: unknown = null
        let rawText: string | null = null
        try {
          rawText = await response.text()
        } catch {
          // ignore
        }
        const trimmedText = rawText?.trim() ?? ''
        if (trimmedText.length > 0 && !trimmedText.startsWith('<')) {
          try {
            errorBody = JSON.parse(trimmedText) as unknown
            if (isErrorWithMessage(errorBody)) {
              errorMessage = errorBody.message
            }
            const parsedCode = getErrorCode(errorBody)
            if (parsedCode) {
              errorCode = parsedCode
            }
          } catch {
            errorMessage = trimmedText.length > 200 ? `${trimmedText.slice(0, 200)}…` : trimmedText
          }
        }
        if (debugApi) {
          logger.debug('API Error response details:', {
            url,
            status: response.status,
            requestId,
            body: errorBody,
            text: trimmedText.length > 0 ? (trimmedText.length > 1000 ? `${trimmedText.slice(0, 1000)}…` : trimmedText) : null,
          })
        }
        const finalMessage = requestId ? `${errorMessage} (requestId: ${requestId})` : errorMessage
        throw new ApiError(finalMessage, response.status, errorCode)
      }

      const data = await this.parseJsonBody<T>(response)
      logger.debug('API Response:', { url, status: response.status })
      if (debugApi) {
        logger.debug('API Response details:', {
          url,
          status: response.status,
          requestId:
            response.headers.get('x-request-id') ??
            response.headers.get('x-amzn-requestid') ??
            response.headers.get('x-amz-request-id') ??
            null,
        })
      }

      return { data, response }
    } catch (error) {
      if (combinedSignal.aborted) {
        if (timeoutController.signal.aborted && !externalSignal?.aborted) {
          logger.warn('API Request timed out', { url, timeoutMs })
        }
        throw error instanceof Error && error.name === 'AbortError' ? error : makeAbortError()
      }

      logger.error('API Request failed:', { url, error })
      throw error
    } finally {
      clearTimeout(timeoutId)
    }
  }

  async get<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const config: RequestConfig = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      timeout: options.timeout,
      signal: options.signal,
    };

    // Add authentication if required
    if (options.withAuth) {
      const token = getAuthToken();
      if (token) {
        config.headers!.Authorization = `Bearer ${token.token}`;
      }
    }

    return this.request<T>(endpoint, config);
  }

  async post<T>(endpoint: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    const config: RequestConfig = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      body,
      timeout: options.timeout,
      signal: options.signal,
    };

    // Add authentication if required
    if (options.withAuth) {
      const token = getAuthToken();
      if (token) {
        config.headers!.Authorization = `Bearer ${token.token}`;
      }
    }

    return this.request<T>(endpoint, config);
  }

  async postForm<T>(endpoint: string, body: FormData, options: RequestOptions = {}): Promise<T> {
    const config: RequestConfig = {
      method: 'POST',
      headers: {
        ...options.headers,
      },
      body,
      timeout: options.timeout,
      signal: options.signal,
    }

    if (options.withAuth) {
      const token = getAuthToken()
      if (token) {
        config.headers = config.headers ?? {}
        config.headers.Authorization = `Bearer ${token.token}`
      }
    }

    return this.request<T>(endpoint, config)
  }

  async postWithResponse<T>(
    endpoint: string,
    body?: unknown,
    options: RequestOptions = {},
  ): Promise<{ data: T; response: Response }> {
    const config: RequestConfig = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      body,
      timeout: options.timeout,
      signal: options.signal,
    }

    if (options.withAuth) {
      const token = getAuthToken()
      if (token) {
        config.headers!.Authorization = `Bearer ${token.token}`
      }
    }

    return this.requestWithResponse<T>(endpoint, config)
  }

  async getWithResponse<T>(
    endpoint: string,
    options: RequestOptions = {},
  ): Promise<{ data: T; response: Response }> {
    const config: RequestConfig = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      timeout: options.timeout,
      signal: options.signal,
    }

    if (options.withAuth) {
      const token = getAuthToken()
      if (token) {
        config.headers!.Authorization = `Bearer ${token.token}`
      }
    }

    return this.requestWithResponse<T>(endpoint, config)
  }

  async put<T>(endpoint: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    const config: RequestConfig = {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      body,
      timeout: options.timeout,
      signal: options.signal,
    };

    // Add authentication if required
    if (options.withAuth) {
      const token = getAuthToken();
      if (token) {
        config.headers!.Authorization = `Bearer ${token.token}`;
      }
    }

    return this.request<T>(endpoint, config);
  }

  async delete<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const config: RequestConfig = {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      timeout: options.timeout,
      signal: options.signal,
    }

    if (options.withAuth) {
      const token = getAuthToken()
      if (token) {
        config.headers!.Authorization = `Bearer ${token.token}`
      }
    }

    return this.request<T>(endpoint, config)
  }
}

export const apiClient = new ApiClient(); 